import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { PageHero, CTABand, Reveal } from "@/components/ui-bits";
import { useCategories, useProducts } from "@/hooks/useContent";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const { data: categories } = useCategories();
  const { data: products, isLoading } = useProducts({ category, q: query });

  const activeCategory = (categories || []).find((c) => c.slug === category);

  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("category", slug);
    else next.delete("category");
    setParams(next, { replace: true });
  };

  return (
    <>
      <Seo
        title={activeCategory ? `${activeCategory.name} Exporter from India | Orvella Overseas` : "Food & Agricultural Products from India | Orvella Overseas"}
        description={activeCategory ? activeCategory.description : "Browse Orvella Overseas' portfolio of Indian food and agricultural products — rice, spices, makhana, dehydrated products, peanuts, tea and food powders — for international B2B supply."}
        path="/products"
      />

      <PageHero
        eyebrow="Our Product Portfolio"
        title={activeCategory ? activeCategory.name : "Food & Agricultural Products from India"}
        sub={activeCategory ? activeCategory.description : "Quality Food & Agricultural Products from India — sourced for importers, distributors, wholesalers, retailers and food businesses worldwide."}
      />

      <section className="section-pad">
        <div className="container-x">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2" data-testid="category-filter-chips">
              <button
                onClick={() => setCategory("")}
                data-testid="filter-all-products"
                className={`chip !px-4 !py-2 transition-colors ${!category ? "!border-gold !bg-gold/10 !text-gold-dark" : "hover:border-gold"}`}
              >
                All Products
              </button>
              {(categories || []).map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setCategory(c.slug)}
                  data-testid={`filter-${c.slug}`}
                  className={`chip !px-4 !py-2 transition-colors ${category === c.slug ? "!border-gold !bg-gold/10 !text-gold-dark" : "hover:border-gold"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <form
              className="relative w-full md:w-72"
              onSubmit={(e) => {
                e.preventDefault();
                setQuery(search);
              }}
              data-testid="product-search-form"
            >
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mist" />
              <input
                className="field !pl-10"
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="product-search-input"
                aria-label="Search products"
              />
            </form>
          </div>

          {isLoading ? (
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card h-80 animate-pulse bg-white/60" />
              ))}
            </div>
          ) : (products || []).length === 0 ? (
            <div className="card mt-12 p-10 text-center" data-testid="products-empty-state">
              <h3 className="font-serif text-xl font-semibold text-navy">No products match your search</h3>
              <p className="mt-2 text-sm text-mist">
                Try a different keyword or category — or contact our export team with your requirement and we will confirm availability.
              </p>
            </div>
          ) : (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
              {products.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.05}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTABand />
    </>
  );
}
